import { useMemo } from 'react';

import { LIVING_SITUATIONS, REAL_ESTATE_USAGES } from 'cpool-constants';
import type { ApplicantCreation } from 'cpool-schemas';
import type { Control } from 'react-hook-form';
import { useWatch } from 'react-hook-form';

export type WohnartValidationError = 'NO_SELF_OCCUPIED_REAL_ESTATE' | 'LIVING_IN_OWN_REAL_ESTATE' | null;

type Props = {
    control: Control<ApplicantCreation>;
};

export const useWohnartValidation = ({ control }: Props) => {
    const wohnart = useWatch<ApplicantCreation, 'wohnsituation.wohnart'>({
        control,
        name: 'wohnsituation.wohnart',
    });

    const immobilien = useWatch<ApplicantCreation, 'immobilien'>({
        control,
        name: 'immobilien',
    });

    const validationError = useMemo((): WohnartValidationError => {
        const hasSelfOccupiedRealEstate = immobilien?.some(
            (immobilie) =>
                immobilie.nutzungsart === REAL_ESTATE_USAGES.EIGENGENUTZT ||
                immobilie.nutzungsart === REAL_ESTATE_USAGES.EIGENGENUTZT_UND_VERMIETET,
        );

        // If living in own house but no self-occupied real estate exists
        if (wohnart === LIVING_SITUATIONS.IM_EIGENEN_HAUS && !hasSelfOccupiedRealEstate) {
            return 'NO_SELF_OCCUPIED_REAL_ESTATE';
        }

        // If NOT living in own house but has self-occupied real estate
        if (wohnart !== null && wohnart !== LIVING_SITUATIONS.IM_EIGENEN_HAUS && hasSelfOccupiedRealEstate) {
            return 'LIVING_IN_OWN_REAL_ESTATE';
        }

        return null;
    }, [wohnart, immobilien]);

    return {
        hasError: validationError !== null,
        validationError,
    };
};
